import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { mainSiteUrl, categories } from '@/mocks/categories';

const guideLinks = [
  { name: 'Complete Guide', path: '/sugargoo-spreadsheet-guide' },
  { name: 'Shipping Cost', path: '/sugargoo-shipping-cost' },
  { name: 'Is Sugargoo Legit', path: '/is-sugargoo-legit' },
  { name: 'Coupon & Discount', path: '/sugargoo-coupon' },
  { name: 'Warehouse Guide', path: '/sugargoo-warehouse' },
  { name: 'vs Competitors', path: '/sugargoo-vs-competitors' },
  { name: 'Best Finds', path: '/sugargoo-best-finds' },
  { name: 'Beginner Tutorial', path: '/sugargoo-beginner-tutorial' },
  { name: 'Save Money Tips', path: '/sugargoo-save-money' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const closeAll = () => {
    setMobileMenuOpen(false);
    setMobileGuideOpen(false);
    setMobileCategoriesOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2 group"
          onClick={closeAll}
        >
          <span className="text-xl md:text-2xl font-heading font-bold text-foreground-900 tracking-tight">
            Sugargoo<span className="text-primary-500">Sheet</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/"
            className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            <i className="ri-home-4-line text-sm"></i>
            Home
          </Link>

          <a
            href="#categories"
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Categories
          </a>

          <div ref={guideRef} className="relative">
            <button
              onClick={() => setGuideDropdownOpen(!guideDropdownOpen)}
              className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-1"
            >
              Shopping Guide
              <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${guideDropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {guideDropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 z-50">
                {guideLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setGuideDropdownOpen(false)}
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors duration-150 cursor-pointer whitespace-nowrap"
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <a
            href="#articles"
            className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Articles
          </a>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer"
          >
            Buy It Now
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-900 cursor-pointer"
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
          <Link
            to="/"
            onClick={closeAll}
            className="flex items-center gap-2 text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors duration-200 py-2.5 cursor-pointer"
          >
            <i className="ri-home-4-line text-base"></i>
            Home
          </Link>

          <button
            onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
            className="flex items-center justify-between w-full text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 py-2.5 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-grid-line text-base"></i>
              Product Categories
            </span>
            <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileCategoriesOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {mobileCategoriesOpen && (
            <div className="ml-4 pl-3 border-l-2 border-background-200/60 flex flex-col gap-0.5 mb-1">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeAll}
                  className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 py-1.5 cursor-pointer whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          )}

          <button
            onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
            className="flex items-center justify-between w-full text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 py-2.5 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-book-open-line text-base"></i>
              Shopping Guide
            </span>
            <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileGuideOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {mobileGuideOpen && (
            <div className="ml-4 pl-3 border-l-2 border-background-200/60 flex flex-col gap-0.5 mb-1">
              {guideLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={closeAll}
                  className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 py-1.5 cursor-pointer whitespace-nowrap"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          )}

          <a
            href="#articles"
            onClick={closeAll}
            className="flex items-center gap-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 py-2.5 cursor-pointer"
          >
            <i className="ri-article-line text-base"></i>
            SEO Articles
          </a>

          <div className="pt-3 mt-2 border-t border-background-200/60">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}